import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Minus, Plus, ShoppingCart, Truck, Shield, Star, Package } from "lucide-react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { API, useAuth, useCart } from "../App";
import { toast } from "sonner";
import { motion } from "framer-motion";

const ProductDetailPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [allProducts, setAllProducts] = useState([]);
  const { user, login } = useAuth();
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productRes, allProductsRes] = await Promise.all([
          axios.get(`${API}/products/${productId}`),
          axios.get(`${API}/products`)
        ]);
        setProduct(productRes.data);
        setAllProducts(allProductsRes.data);
      } catch (error) {
        console.error("Error fetching product:", error);
        toast.error("Product not found");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [productId]);

  const handleAddToCart = async () => {
    if (!user) {
      toast.error("Please login to add items to cart");
      return;
    }
    const success = await addToCart(product.product_id, quantity);
    if (success) {
      setQuantity(1);
    }
  };

  const formatWeight = (weight) => {
    const weightStr = String(weight).toLowerCase().trim();

    if (weightStr === "1000" || weightStr === "1000g" || weightStr === "1kg") {
      return "1kg";
    }

    if (weightStr === "250" || weightStr === "250g" || weightStr === "250gm") {
      return "250g";
    }

    if (weightStr === "500" || weightStr === "500g" || weightStr === "500gm") {
      return "500g";
    }

    const weightNum = Number(weight);
    if (!Number.isNaN(weightNum)) {
      if (weightNum >= 1000) return `${weightNum / 1000}kg`;
      return `${weightNum}g`;
    }

    return weight;
  };

  const fallbackImage = "/images/story-chilli.jpg";

  const getProductImage = (item) => {
    const weightStr = String(item?.weight).toLowerCase().trim();

    if (
      weightStr === "250" ||
      weightStr === "250g" ||
      weightStr === "250gm"
    ) {
      return "/images/btchilli-250gm.png";
    }

    if (
      weightStr === "500" ||
      weightStr === "500g" ||
      weightStr === "500gm"
    ) {
      return "/images/btchilli-500gm.png";
    }

    if (
      weightStr === "1000" ||
      weightStr === "1000g" ||
      weightStr === "1kg"
    ) {
      return "/images/btchilli-1kg.png";
    }

    return fallbackImage;
  };

  const otherSizes = allProducts.filter(
    (p) => String(p.product_id) !== String(productId)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 pb-16 max-w-7xl mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 w-32 bg-card rounded mb-8"></div>
            <div className="grid md:grid-cols-2 gap-12">
              <div className="aspect-square bg-card rounded-xl"></div>
              <div className="space-y-4">
                <div className="h-6 w-24 bg-card rounded"></div>
                <div className="h-10 w-3/4 bg-card rounded"></div>
                <div className="h-20 bg-card rounded"></div>
                <div className="h-12 w-48 bg-card rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">Product Not Found</h1>
          <Link to="/products">
            <Button className="rounded-full">Back to Products</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="product-detail-page">
      <Header />

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Products
          </Link>

          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="aspect-square rounded-xl overflow-hidden bg-card border border-white/5 flex items-center justify-center p-6">
                <img
                  src={getProductImage(product)}
                  alt={product.name}
                  className="w-full h-full object-contain"
                  data-testid="product-image"
                  onError={(e) => {
                    e.currentTarget.src = fallbackImage;
                  }}
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <span className="badge-premium">{formatWeight(product.weight)}</span>
                  <span className="text-xs text-muted-foreground font-mono">{product.sku}</span>
                  {product.stock <= product.low_stock_threshold && product.stock > 0 && (
                    <span className="badge-low-stock text-xs px-2 py-1 rounded">
                      Only {product.stock} left
                    </span>
                  )}
                </div>

                <h1 className="text-3xl font-bold text-foreground" data-testid="product-name">
                  {product.name}
                </h1>

                <div className="flex items-center gap-2">
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-4 h-4 fill-secondary text-secondary" />
                    ))}
                  </div>
                  <span className="text-sm text-muted-foreground">(128 reviews)</span>
                </div>

                <div className="space-y-1">
                  <div className="text-4xl font-bold text-foreground" data-testid="product-price">
                    ₹{product.price}
                  </div>
                  <div className="text-sm text-muted-foreground">₹{product.price_per_100g}/100g</div>
                </div>

                <p className="text-muted-foreground leading-relaxed">{product.description}</p>

                {product.stock > 0 ? (
                  <div className="flex items-center gap-2 text-green-500">
                    <Package className="w-4 h-4" />
                    <span className="text-sm font-medium">In Stock ({product.stock} available)</span>
                  </div>
                ) : (
                  <div className="text-red-500 text-sm font-medium">Out of Stock</div>
                )}

                {product.stock > 0 && (
                  <div className="space-y-4 pt-4">
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-muted-foreground">Quantity:</span>
                      <div className="flex items-center gap-3">
                        <button
                          className="qty-btn"
                          onClick={() => setQuantity(Math.max(1, quantity - 1))}
                          disabled={quantity <= 1}
                          data-testid="qty-decrease"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-12 text-center font-medium text-foreground" data-testid="qty-display">
                          {quantity}
                        </span>
                        <button
                          className="qty-btn"
                          onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                          disabled={quantity >= product.stock}
                          data-testid="qty-increase"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      {user ? (
                        <Button
                          onClick={handleAddToCart}
                          className="flex-1 bg-primary hover:bg-primary/90 text-white rounded-full py-6 text-base glow-red"
                          data-testid="add-to-cart-btn"
                        >
                          <ShoppingCart className="w-5 h-5 mr-2" />
                          Add to Cart - ₹{product.price * quantity}
                        </Button>
                      ) : (
                        <Button
                          onClick={login}
                          className="flex-1 bg-primary hover:bg-primary/90 text-white rounded-full py-6 text-base"
                          data-testid="login-to-buy-btn"
                        >
                          Sign In to Buy
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 pt-6 border-t border-white/5">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-card">
                      <Truck className="w-5 h-5 text-secondary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">Free Delivery</p>
                      <p className="text-xs text-muted-foreground">On orders above ₹500</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-card">
                      <Shield className="w-5 h-5 text-secondary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">Quality Assured</p>
                      <p className="text-xs text-muted-foreground">Lab tested purity</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {otherSizes.length > 0 && (
            <div className="mt-16">
              <h2 className="text-2xl font-bold text-foreground mb-6">Other Pack Sizes</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {otherSizes.map((p) => (
                  <Link
                    key={p.product_id}
                    to={`/products/${p.product_id}`}
                    className="bg-card border border-white/5 rounded-xl p-4 flex items-center gap-4 hover:border-primary/30 transition-colors"
                    data-testid={`other-size-${p.product_id}`}
                  >
                    <img
                      src={getProductImage(p)}
                      alt={p.name}
                      className="w-20 h-20 rounded-lg object-contain bg-black p-2"
                      onError={(e) => {
                        e.currentTarget.src = fallbackImage;
                      }}
                    />
                    <div>
                      <span className="badge-premium text-xs">{formatWeight(p.weight)}</span>
                      <p className="text-lg font-bold text-foreground mt-1">₹{p.price}</p>
                      <p className="text-xs text-muted-foreground">₹{p.price_per_100g}/100g</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;